import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/redux/store";

interface Visitor {
  id: string;
  session_id: string;
  widget_id: string;
  page_url: string;
  page_title: string;
  referrer?: string;
  ip_address?: string;
  user_agent?: string;
  country?: string;
  city?: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  metadata?: Record<string, any>;
  first_seen: string;
  last_seen: string;
  is_active: boolean;
  total_page_views: number;
  time_on_site: number;
}

interface VisitorsState {
  visitors: Visitor[];
  activeVisitors: Visitor[];
  selectedVisitor: Visitor | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: VisitorsState = {
  visitors: [],
  activeVisitors: [],
  selectedVisitor: null,
  isLoading: false,
  error: null,
};

const visitorsSlice = createSlice({
  name: "visitors",
  initialState,
  reducers: {
    setVisitors: (state, action: PayloadAction<Visitor[]>) => {
      state.visitors = action.payload;
      state.activeVisitors = action.payload.filter((v) => v.is_active);
    },
    addVisitor: (state, action: PayloadAction<Visitor>) => {
      const exists = state.visitors.find((v) => v.id === action.payload.id);
      if (!exists) {
        state.visitors.unshift(action.payload);
        if (action.payload.is_active) {
          // Check if already in activeVisitors to prevent duplicates
          const activeExists = state.activeVisitors.find((v) => v.id === action.payload.id);
          if (!activeExists) {
            state.activeVisitors.unshift(action.payload);
          }
        }
      } else {
        // Update existing visitor instead of skipping
        const index = state.visitors.findIndex((v) => v.id === action.payload.id);
        if (index !== -1) {
          state.visitors[index] = action.payload;
        }
        // Update activeVisitors accordingly
        const activeIndex = state.activeVisitors.findIndex((v) => v.id === action.payload.id);
        if (action.payload.is_active && activeIndex === -1) {
          state.activeVisitors.unshift(action.payload);
        } else if (!action.payload.is_active && activeIndex !== -1) {
          state.activeVisitors.splice(activeIndex, 1);
        } else if (action.payload.is_active && activeIndex !== -1) {
          state.activeVisitors[activeIndex] = action.payload;
        }
      }
    },
    updateVisitor: (state, action: PayloadAction<Visitor>) => {
      const index = state.visitors.findIndex((v) => v.id === action.payload.id);
      if (index !== -1) {
        state.visitors[index] = action.payload;
      }

      const activeIndex = state.activeVisitors.findIndex((v) => v.id === action.payload.id);
      if (action.payload.is_active && activeIndex === -1) {
        state.activeVisitors.push(action.payload);
      } else if (!action.payload.is_active && activeIndex !== -1) {
        state.activeVisitors.splice(activeIndex, 1);
      } else if (action.payload.is_active && activeIndex !== -1) {
        state.activeVisitors[activeIndex] = action.payload;
      }
    },
    markVisitorInactive: (state, action: PayloadAction<{ id: string; last_seen?: string }>) => {
      const { id, last_seen } = action.payload;

      const index = state.visitors.findIndex((v) => v.id === id);
      if (index !== -1) {
        state.visitors[index] = {
          ...state.visitors[index],
          is_active: false,
          ...(last_seen ? { last_seen } : {}),
        };
      }

      state.activeVisitors = state.activeVisitors.filter((v) => v.id !== id);

      if (state.selectedVisitor?.id === id) {
        state.selectedVisitor = null;
      }
    },
    removeVisitor: (state, action: PayloadAction<string>) => {
      state.visitors = state.visitors.filter((v) => v.id !== action.payload);
      state.activeVisitors = state.activeVisitors.filter((v) => v.id !== action.payload);
      if (state.selectedVisitor?.id === action.payload) {
        state.selectedVisitor = null;
      }
    },
    selectVisitor: (state, action: PayloadAction<Visitor | null>) => {
      state.selectedVisitor = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setVisitors,
  addVisitor,
  updateVisitor,
  markVisitorInactive,
  removeVisitor,
  selectVisitor,
  setLoading,
  setError,
} = visitorsSlice.actions;

export const selectAllVisitors = (state: RootState) => state.visitors.visitors;
export const selectActiveVisitors = (state: RootState) => state.visitors.activeVisitors;
export const selectSelectedVisitor = (state: RootState) => state.visitors.selectedVisitor;
export const selectVisitorsLoading = (state: RootState) => state.visitors.isLoading;

export default visitorsSlice.reducer;
